import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { usersService, operadorasService, notificacoesService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Usuarios.css';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50];
const ROLES_COM_OPERADORAS = ['backoffice', 'vendedor'];

const Usuarios = () => {
  const navigate = useNavigate();
  const { user, isAdminOrDiretor } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [showNovoUsuario, setShowNovoUsuario] = useState(false);
  const [operadoras, setOperadoras] = useState([]);
  const [usuarioOperadoras, setUsuarioOperadoras] = useState([]);
  const [atenderTodas, setAtenderTodas] = useState(false);
  const [novoUsuarioRole, setNovoUsuarioRole] = useState('');
  const [novoUsuarioPix, setNovoUsuarioPix] = useState('');
  const [supervisoresSubordinados, setSupervisoresSubordinados] = useState([]); // ids dos supervisores subordinados ao coordenador
  const [busca, setBusca] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estados para notificações
  const [notificacoes, setNotificacoes] = useState([]);
  const [notificacoesCount, setNotificacoesCount] = useState(0);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const [carregandoNotificacoes, setCarregandoNotificacoes] = useState(false);
  
  // Estados para consultas de viabilidade
  const [showConsultasModal, setShowConsultasModal] = useState(false);
  const [usuarioSelecionadoParaConsultas, setUsuarioSelecionadoParaConsultas] = useState(null);
  const [consultasViabilidade, setConsultasViabilidade] = useState([]);
  const [resumoConsultas, setResumoConsultas] = useState(null);
  const [carregandoConsultas, setCarregandoConsultas] = useState(false);
  const [mesReferencia, setMesReferencia] = useState(new Date().getMonth() + 1);
  const [anoReferencia, setAnoReferencia] = useState(new Date().getFullYear());
  
  // Estados para inatividade
  const [showInatividadeModal, setShowInatividadeModal] = useState(false);
  const [inatividadeStatus, setInatividadeStatus] = useState(null);
  const [carregandoInatividade, setCarregandoInatividade] = useState(false);
  const [usuariosSelecionadosInatividade, setUsuariosSelecionadosInatividade] = useState([]);
  const [inativando, setInativando] = useState(false);

  useEffect(() => {
    loadUsuarios();
    loadOperadoras();
    if (isAdminOrDiretor()) {
      loadNotificacoes();
    }
  }, []);

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(data);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const loadNotificacoes = async () => {
    if (!isAdminOrDiretor()) return;
    
    try {
      setCarregandoNotificacoes(true);
      const data = await notificacoesService.getAll({ lida: 'false', limit: 20 });
      setNotificacoes(data.notificacoes || []);
      setNotificacoesCount(data.naoLidasCount || 0);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setCarregandoNotificacoes(false);
    }
  };

  const handleMarcarNotificacaoLida = async (id) => {
    try {
      await notificacoesService.markAsRead(id);
      loadNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleMarcarTodasLidas = async () => {
    try {
      await notificacoesService.markAllAsRead();
      setNotificacoes([]);
      setNotificacoesCount(0);
      setShowNotificacoes(false);
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const handleResetarOTP = async (usuarioId, usuarioNome) => {
    const confirmacao = window.confirm(
      `Tem certeza que deseja resetar o OTP do usuário "${usuarioNome}"?\n\n` +
      `O usuário precisará configurar o Google Authenticator novamente no próximo login.`
    );
    
    if (!confirmacao) return;

    try {
      await usersService.resetOtp(usuarioId);
      alert('✅ OTP resetado com sucesso!');
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao resetar OTP:', error);
      alert('❌ Erro ao resetar OTP: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleReativarUsuario = async (usuarioId, usuarioNome) => {
    const confirmacao = window.confirm(
      `Tem certeza que deseja reativar o usuário "${usuarioNome}"?`
    );
    
    if (!confirmacao) return;

    try {
      await usersService.reativarUsuario(usuarioId);
      alert('✅ Usuário reativado com sucesso!');
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao reativar usuário:', error);
      alert('❌ Erro ao reativar usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usersService.getAll();
      setUsuarios(data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Carregar consultas de viabilidade de um usuário
  const loadConsultasViabilidade = async (userId, mes, ano) => {
    try {
      setCarregandoConsultas(true);
      const [consultasData, resumoData] = await Promise.all([
        usersService.getViabilidadeConsultas(userId, 100, mes, ano),
        usersService.getViabilidadeResumo(userId, mes, ano)
      ]);
      setConsultasViabilidade(consultasData.items || []);
      setResumoConsultas(resumoData);
    } catch (error) {
      console.error('Erro ao carregar consultas de viabilidade:', error);
      alert('Erro ao carregar consultas de viabilidade');
    } finally {
      setCarregandoConsultas(false);
    }
  };

  // Carregar status de inatividade (Admin/Diretor)
  const loadInatividadeStatus = async () => {
    try {
      setCarregandoInatividade(true);
      const data = await usersService.getInatividadeStatus();
      setInatividadeStatus(data);
    } catch (error) {
      console.error('Erro ao carregar status de inatividade:', error);
      alert('Erro ao carregar status de inatividade');
    } finally {
      setCarregandoInatividade(false);
    }
  };

  const getRoleText = (role) => {
    const roles = {
      'admin': 'Administrador',
      'diretor': 'Diretor',
      'coordenador': 'Coordenador',
      'supervisor': 'Supervisor',
      'vendedor': 'Vendedor',
      'backoffice': 'BackOffice',
      'supervisor_bko': 'Supervisor BKO'
    };
    return roles[role] || role;
  };

  const usuariosFiltrados = useMemo(() => {
    if (!busca.trim()) return usuarios;
    const term = busca.trim().toLowerCase();
    return usuarios.filter((u) => {
      const nome = (u.nome || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      const roleText = getRoleText(u.role).toLowerCase();
      const supervisorNome = (u.supervisor?.nome || '').toLowerCase();
      return nome.includes(term) || username.includes(term) || roleText.includes(term) || supervisorNome.includes(term);
    });
  }, [usuarios, busca]);

  const totalPages = Math.max(1, Math.ceil(usuariosFiltrados.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const usuariosPagina = useMemo(
    () => usuariosFiltrados.slice(pageStart, pageStart + itemsPerPage),
    [usuariosFiltrados, pageStart, itemsPerPage]
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const getRoleClass = (role) => {
    const classes = {
      'admin': 'role-admin',
      'diretor': 'role-diretor',
      'coordenador': 'role-coordenador',
      'supervisor': 'role-supervisor',
      'vendedor': 'role-vendedor',
      'backoffice': 'role-backoffice',
      'supervisor_bko': 'role-supervisor-bko'
    };
    return classes[role] || '';
  };

  const handleDelete = async (usuario) => {
    // Confirmação de exclusão
    const confirmMessage = `⚠️ ATENÇÃO!\n\nVocê está prestes a excluir:\n👤 Nome: ${usuario.nome}\n🔑 Usuário: ${usuario.username}\n\nEsta ação NÃO pode ser desfeita!\n\nDigite "EXCLUIR" para confirmar:`;
    
    const confirmation = prompt(confirmMessage);
    
    if (confirmation !== 'EXCLUIR') {
      alert('❌ Operação cancelada');
      return;
    }

    try {
      await usersService.delete(usuario.id);
      alert('✅ Usuário excluído com sucesso!');
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('❌ Erro ao excluir usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleEdit = async (usuario) => {
    setEditingUsuario({ ...usuario });
    
    if (ROLES_COM_OPERADORAS.includes(usuario.role)) {
      try {
        const data = await usersService.getOperadoras(usuario.id);
        setUsuarioOperadoras(data.operadoras.map(op => op.id));
        setAtenderTodas(data.operadoras.length === 0);
      } catch (error) {
        console.error('Erro ao carregar operadoras do usuário:', error);
        setUsuarioOperadoras([]);
        setAtenderTodas(false);
      }
      setSupervisoresSubordinados([]);
    } else if (usuario.role === 'coordenador') {
      // Carregar supervisores subordinados ao coordenador
      const subordinados = usuarios.filter(u => u.role === 'supervisor' && u.supervisor_id === usuario.id);
      setSupervisoresSubordinados(subordinados.map(s => s.id));
      setUsuarioOperadoras([]);
      setAtenderTodas(false);
    } else {
      setUsuarioOperadoras([]);
      setAtenderTodas(false);
      setSupervisoresSubordinados([]);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        nome: editingUsuario.nome,
        role: editingUsuario.role,
        supervisor_id: editingUsuario.supervisor_id || null,
        ativo: editingUsuario.ativo !== undefined ? editingUsuario.ativo : true,
        pix: editingUsuario.role === 'vendedor' ? (editingUsuario.pix || '').trim() || null : null
      };

      // Se tiver nova senha, adicionar
      if (editingUsuario.nova_senha && editingUsuario.nova_senha.trim() !== '') {
        if (editingUsuario.nova_senha.length < 6) {
          alert('❌ A senha deve ter no mínimo 6 caracteres');
          return;
        }
        updateData.password = editingUsuario.nova_senha;
      }

      await usersService.update(editingUsuario.id, updateData);
      
      // Se for coordenador, atualizar supervisores subordinados
      if (editingUsuario.role === 'coordenador') {
        try {
          await usersService.updateSupervisoresSubordinados(editingUsuario.id, supervisoresSubordinados);
        } catch (err) {
          console.error('Erro ao atualizar supervisores subordinados:', err);
          alert('⚠️ Usuário atualizado, mas houve erro ao atualizar supervisores subordinados');
        }
      }
      
      // Se for backoffice ou vendedor, atualizar operadoras
      if (ROLES_COM_OPERADORAS.includes(editingUsuario.role)) {
        try {
          await usersService.updateOperadoras(
            editingUsuario.id,
            atenderTodas ? [] : usuarioOperadoras,
            atenderTodas
          );
        } catch (error) {
          console.error('Erro ao atualizar operadoras:', error);
          alert('⚠️ Usuário atualizado, mas houve erro ao atualizar operadoras');
        }
      }
      
      alert('✅ Usuário atualizado com sucesso!');
      setEditingUsuario(null);
      setUsuarioOperadoras([]);
      setAtenderTodas(false);
      setSupervisoresSubordinados([]);
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('❌ Erro ao atualizar usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const role = formData.get('role');
      const userData = {
        username: formData.get('username'),
        password: formData.get('password'),
        nome: formData.get('nome'),
        role,
        supervisor_id: role === 'coordenador' ? null : (formData.get('supervisor_id') || null),
        pix: role === 'vendedor' ? (formData.get('pix') || '').trim() || null : null
      };
      if (role === 'coordenador' && supervisoresSubordinados.length > 0) {
        userData.supervisores_subordinados = supervisoresSubordinados;
      }

      if (userData.password.length < 6) {
        alert('❌ A senha deve ter no mínimo 6 caracteres');
        return;
      }

      await usersService.create(userData);
      alert('✅ Usuário criado com sucesso!');
      setShowNovoUsuario(false);
      setNovoUsuarioPix('');
      setSupervisoresSubordinados([]);
      loadUsuarios();
      e.target.reset();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      alert('❌ Erro ao criar usuário: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const closeModal = () => {
    setEditingUsuario(null);
    setShowNovoUsuario(false);
    setUsuarioOperadoras([]);
    setAtenderTodas(false);
    setNovoUsuarioRole('');
    setNovoUsuarioPix('');
    setSupervisoresSubordinados([]);
  };

  // Modal de consultas de viabilidade
  const openConsultasModal = async (usuario) => {
    setUsuarioSelecionadoParaConsultas(usuario);
    setMesReferencia(new Date().getMonth() + 1);
    setAnoReferencia(new Date().getFullYear());
    setShowConsultasModal(true);
    // Carregar dados
    await loadConsultasViabilidade(usuario.id, new Date().getMonth() + 1, new Date().getFullYear());
  };

  const closeConsultasModal = () => {
    setShowConsultasModal(false);
    setUsuarioSelecionadoParaConsultas(null);
    setConsultasViabilidade([]);
    setResumoConsultas(null);
  };

  // Modal de inatividade (Admin/Diretor)
  const openInatividadeModal = async () => {
    setShowInatividadeModal(true);
    await loadInatividadeStatus();
  };

  const closeInatividadeModal = () => {
    setShowInatividadeModal(false);
    setInatividadeStatus(null);
    setUsuariosSelecionadosInatividade([]);
  };

  // Toggle de selecao de usuario para inativacao
  const toggleUsuarioInatividade = (userId) => {
    setUsuariosSelecionadosInatividade(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Toggle selecionar todos
  const toggleTodosInatividade = () => {
    if (!inatividadeStatus?.vendedoresInativos) return;
    
    if (usuariosSelecionadosInatividade.length === inatividadeStatus.vendedoresInativos.length) {
      setUsuariosSelecionadosInatividade([]);
    } else {
      setUsuariosSelecionadosInatividade(inatividadeStatus.vendedoresInativos.map(v => v.id));
    }
  };

  // Inativar usuarios selecionados
  const inativarSelecionados = async () => {
    if (usuariosSelecionadosInatividade.length === 0) {
      alert('Selecione ao menos um usuario para inativar');
      return;
    }
    
    const confirmacao = window.confirm(`Tem certeza que deseja inativar ${usuariosSelecionadosInatividade.length} usuario(s)?`);
    if (!confirmacao) return;
    
    try {
      setInativando(true);
      await usersService.inativarUsuarios(usuariosSelecionadosInatividade);
      alert('Usuarios inativados com sucesso!');
      setUsuariosSelecionadosInatividade([]);
      await loadInatividadeStatus();
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao inativar usuarios:', error);
      alert('Erro ao inativar usuarios: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setInativando(false);
    }
  };

  const handleOperadoraToggle = (operadoraId) => {
    if (atenderTodas) {
      setAtenderTodas(false);
    }
    if (usuarioOperadoras.includes(operadoraId)) {
      setUsuarioOperadoras(usuarioOperadoras.filter(id => id !== operadoraId));
    } else {
      setUsuarioOperadoras([...usuarioOperadoras, operadoraId]);
    }
  };

  const handleAtenderTodasChange = (checked) => {
    setAtenderTodas(checked);
    if (checked) {
      setUsuarioOperadoras([]);
    }
  };

  return (
    <Layout>
      <div className="usuarios-page">
        {/* Header */}
        <div className="usuarios-header">
          <div>
            <h1 className="card-title">👥 Gerenciamento de Usuários</h1>
          </div>
          <div className="header-actions">
            {/* Botão de Notificações - Apenas Admin/Diretor */}
            {isAdminOrDiretor() && (
              <div style={{ position: 'relative', marginRight: '0.5rem' }}>
                <button 
                  onClick={() => setShowNotificacoes(!showNotificacoes)} 
                  className="btn btn-warning"
                  style={{ position: 'relative' }}
                >
                  🔔 Notificações
                  {notificacoesCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: 'var(--error)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {notificacoesCount}
                    </span>
                  )}
                </button>
                
                {/* Dropdown de Notificações */}
                {showNotificacoes && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'var(--cinza-escuro-1)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    width: '400px',
                    maxHeight: '500px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                  }}>
                    <div style={{ 
                      padding: '1rem', 
                      borderBottom: '1px solid var(--border-color)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>🔔 Notificações</h3>
                      <button
                        onClick={handleMarcarTodasLidas}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          color: 'var(--texto-secundario)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Marcar todas como lidas
                      </button>
                    </div>
                    
                    {carregandoNotificacoes ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
                    ) : notificacoes.length === 0 ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--texto-secundario)' }}>
                        Nenhuma notificação não lida
                      </div>
                    ) : (
                      notificacoes.map(notif => (
                        <div
                          key={notif.id}
                          style={{
                            padding: '1rem',
                            borderBottom: '1px solid var(--border-color)',
                            background: notif.tipo === 'comportamento_suspeito' ? 'rgba(220, 53, 69, 0.1)' : 'transparent'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <strong style={{ 
                              color: notif.tipo === 'comportamento_suspeito' ? 'var(--error)' : 'var(--texto-primario)',
                              fontSize: '0.9rem'
                            }}>
                              {notif.tipo === 'comportamento_suspeito' && '⚠️ '}
                              {notif.usuario_nome}
                            </strong>
                            <button
                              onClick={() => handleMarcarNotificacaoLida(notif.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--texto-secundario)',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              ✓
                            </button>
                          </div>
                          <p style={{ 
                            margin: '0.5rem 0', 
                            fontSize: '0.875rem',
                            color: 'var(--texto-secundario)',
                            lineHeight: 1.4
                          }}>
                            {notif.mensagem}
                          </p>
                          <div style={{ fontSize: '0.75rem', color: 'var(--texto-secundario)' }}>
                            🌐 {notif.ip || 'N/A'} • 📅 {new Date(notif.data_hora).toLocaleString('pt-BR')}
                          </div>
                          {notif.tipo === 'comportamento_suspeito' && (
                            <button
                              onClick={() => handleReativarUsuario(notif.usuario_id, notif.usuario_nome)}
                              style={{
                                marginTop: '0.5rem',
                                background: 'var(--azul-dvs)',
                                color: '#fff',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              🔄 Reativar Usuário
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              🏠 Voltar ao Dashboard
            </button>
            <button onClick={() => setShowNovoUsuario(true)} className="btn btn-primary">
              ➕ Novo Usuário
            </button>
            {/* Botão de Gestão de Inatividade - Apenas Admin/Diretor */}
            {(user?.role === 'admin' || user?.role === 'diretor') && (
              <button onClick={openInatividadeModal} className="btn btn-warning">
                ⚠️ Inatividade
              </button>
            )}
          </div>
        </div>

        {/* Busca e itens por página */}
        {!loading && usuarios.length > 0 && (
          <div className="usuarios-toolbar">
            <div className="usuarios-busca">
              <input
                type="text"
                placeholder="Buscar por nome, usuário, hierarquia ou supervisor..."
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setCurrentPage(1); }}
                className="input-busca"
              />
            </div>
            <div className="usuarios-per-page">
              <label htmlFor="per-page">Itens por página:</label>
              <select
                id="per-page"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabela de Usuários */}
        {loading ? (
          <div className="loading">Carregando usuários...</div>
        ) : usuarios.length === 0 ? (
          <div className="no-usuarios">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
            <h3>Nenhum usuário encontrado</h3>
            <p>Crie o primeiro usuário para começar</p>
            <button
              onClick={() => setShowNovoUsuario(true)}
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
            >
              ➕ Criar Primeiro Usuário
            </button>
          </div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className="no-usuarios">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
            <h3>Nenhum resultado para &quot;{busca}&quot;</h3>
            <p>Tente outro termo de busca</p>
            <button
              type="button"
              onClick={() => { setBusca(''); setCurrentPage(1); }}
              className="btn btn-secondary"
              style={{ marginTop: '1rem' }}
            >
              Limpar busca
            </button>
          </div>
        ) : (
          <>
          <div className="table-responsive">
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Usuário</th>
                  <th>Hierarquia</th>
                  <th>Supervisor</th>
                  <th>Status</th>
                  <th>Status OTP</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuariosPagina.map(usuario => (
                  <tr key={usuario.id}>
                    <td>{usuario.id}</td>
                    <td>{usuario.nome}</td>
                    <td>{usuario.username}</td>
                    <td>
                      <span className={`role-badge ${getRoleClass(usuario.role)}`}>
                        {getRoleText(usuario.role)}
                      </span>
                    </td>
                    <td>
                      {usuario.supervisor ? usuario.supervisor.nome : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.ativo ? 'status-ativo' : 'status-inativo'}`}>
                        {usuario.ativo ? '✅ Ativo' : '❌ Inativo'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.otp_verified ? 'status-ativo' : 'status-inativo'}`} style={{ fontSize: '0.75rem' }}>
                        {usuario.otp_verified ? '✅ Ativo' : '⏳ Pendente'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(usuario)}
                          className="btn-small btn-edit"
                        >
                          Editar
                        </button>
                        {/* Botão de Resetar OTP - Apenas Admin/Diretor */}
                        {isAdminOrDiretor() && (
                          <button
                            onClick={() => handleResetarOTP(usuario.id, usuario.nome)}
                            className="btn-small btn-warning"
                            title="Resetar OTP do usuário"
                            disabled={!usuario.otp_verified}
                          >
                            🔑 Reset OTP
                          </button>
                        )}
                        {/* Botão de Consultas - Apenas vendedores, supervisores e coordenadores */}
                        {(usuario.role === 'vendedor' || usuario.role === 'supervisor' || usuario.role === 'coordenador') && (
                          <button
                            onClick={() => openConsultasModal(usuario)}
                            className="btn-small btn-info"
                            title="Ver consultas de viabilidade"
                          >
                            📊 Consultas
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(usuario)}
                          className="btn-small btn-danger"
                          disabled={usuario.username === 'admin' || usuario.id === user?.id}
                          title={usuario.username === 'admin' ? 'Não é possível excluir o usuário admin' : usuario.id === user?.id ? 'Você não pode excluir seu próprio usuário' : ''}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="usuarios-paginacao">
              <span className="paginacao-info">
                Mostrando {pageStart + 1} a {Math.min(pageStart + itemsPerPage, usuariosFiltrados.length)} de {usuariosFiltrados.length} usuário(s)
              </span>
              <div className="paginacao-buttons">
                <button
                  type="button"
                  className="btn btn-secondary btn-pag"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <span className="paginacao-pages">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-pag"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
          </>
        )}

        {/* Modal de Editar Usuário */}
        {editingUsuario && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-usuario">
                <h2>✏️ Editar Usuário</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <form onSubmit={handleEditSubmit} className="modal-body-usuario">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      value={editingUsuario.nome || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, nome: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Usuário</label>
                    <input
                      type="text"
                      value={editingUsuario.username || ''}
                      disabled
                      style={{ background: 'var(--cinza-escuro-2)', opacity: 0.7 }}
                    />
                    <small>O usuário não pode ser alterado</small>
                  </div>

                  <div className="form-group">
                    <label>Hierarquia *</label>
                    <select
                      value={editingUsuario.role || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, role: e.target.value })}
                      required
                    >
                      {user?.role === 'admin' && <option value="admin">Administrador</option>}
                      <option value="diretor">Diretor</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="vendedor">Vendedor</option>
                      <option value="backoffice">BackOffice</option>
                      <option value="supervisor_bko">Supervisor BKO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={editingUsuario.ativo ? 'true' : 'false'}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, ativo: e.target.value === 'true' })}
                    >
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                  </div>

                  {editingUsuario.role !== 'coordenador' && (
                    <div className="form-group">
                      <label>Supervisor</label>
                      <select
                        value={editingUsuario.supervisor_id || ''}
                        onChange={(e) => setEditingUsuario({ ...editingUsuario, supervisor_id: e.target.value ? parseInt(e.target.value) : null })}
                      >
                        <option value="">Sem supervisor</option>
                        {usuarios
                          .filter(u => {
                            if (u.id === editingUsuario.id) return false;
                            if (editingUsuario.role === 'backoffice') return u.role === 'supervisor_bko';
                            if (editingUsuario.role === 'vendedor') return u.role === 'supervisor';
                            if (editingUsuario.role === 'supervisor') return u.role === 'coordenador' || u.role === 'admin' || u.role === 'diretor';
                            return u.role === 'supervisor' || u.role === 'supervisor_bko' || u.role === 'admin' || u.role === 'diretor' || u.role === 'coordenador';
                          })
                          .map(sup => (
                            <option key={sup.id} value={sup.id}>{sup.nome} ({getRoleText(sup.role)})</option>
                          ))}
                      </select>
                    </div>
                  )}
                  {editingUsuario.role === 'coordenador' && (
                    <div className="form-group full-width">
                      <label>Supervisores subordinados</label>
                      <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem' }}>
                        {usuarios.filter(u => u.role === 'supervisor').map(sup => (
                          <label key={sup.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={supervisoresSubordinados.includes(sup.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSupervisoresSubordinados(prev => [...prev, sup.id]);
                                } else {
                                  setSupervisoresSubordinados(prev => prev.filter(id => id !== sup.id));
                                }
                              }}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {sup.nome}
                          </label>
                        ))}
                        {usuarios.filter(u => u.role === 'supervisor').length === 0 && (
                          <span style={{ color: '#666' }}>Nenhum supervisor cadastrado</span>
                        )}
                      </div>
                      <small>Selecione os supervisores que reportam a este coordenador</small>
                    </div>
                  )}

                  {editingUsuario.role === 'vendedor' && (
                    <div className="form-group">
                      <label>Chave PIX</label>
                      <input
                        type="text"
                        value={editingUsuario.pix || ''}
                        onChange={(e) => setEditingUsuario({ ...editingUsuario, pix: e.target.value })}
                        placeholder="CPF, e-mail ou chave aleatória"
                      />
                      <small>Chave PIX do vendedor para pagamentos (CPF, e-mail ou chave aleatória)</small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Nova Senha (opcional)</label>
                    <input
                      type="password"
                      value={editingUsuario.nova_senha || ''}
                      onChange={(e) => setEditingUsuario({ ...editingUsuario, nova_senha: e.target.value })}
                      minLength="6"
                      placeholder="Deixe em branco para manter a senha atual"
                    />
                    <small>Mínimo 6 caracteres</small>
                  </div>

                  {ROLES_COM_OPERADORAS.includes(editingUsuario.role) && (
                    <div className="form-group full-width">
                      <label>
                        {editingUsuario.role === 'vendedor'
                          ? 'Operadoras para Trabalhar'
                          : 'Operadoras para Atender'}
                      </label>
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={atenderTodas}
                            onChange={(e) => handleAtenderTodasChange(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <strong>Atender Todas as Operadoras</strong>
                        </label>
                        
                        {!atenderTodas && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.5rem' }}>
                            {operadoras.filter(op => op.ativo).map(operadora => (
                              <label
                                key={operadora.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '0.5rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={usuarioOperadoras.includes(operadora.id)}
                                  onChange={() => handleOperadoraToggle(operadora.id)}
                                  style={{ marginRight: '0.5rem' }}
                                />
                                {operadora.nome_display || operadora.nome.charAt(0).toUpperCase() + operadora.nome.slice(1)}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Consultas de Viabilidade */}
        {showConsultasModal && usuarioSelecionadoParaConsultas && (
          <div className="modal-overlay" onClick={closeConsultasModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div className="modal-header-usuario">
                <h2>📊 Consultas de Viabilidade - {usuarioSelecionadoParaConsultas.nome}</h2>
                <span className="close-modal" onClick={closeConsultasModal}>×</span>
              </div>
              
              <div className="modal-body-usuario">
                {/* Filtro por Mês/Ano */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                  <div>
                    <label>Mês:</label>
                    <select 
                      value={mesReferencia} 
                      onChange={(e) => {
                        setMesReferencia(parseInt(e.target.value));
                        loadConsultasViabilidade(usuarioSelecionadoParaConsultas.id, parseInt(e.target.value), anoReferencia);
                      }}
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('pt-BR', { month: 'long' })}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Ano:</label>
                    <select 
                      value={anoReferencia} 
                      onChange={(e) => {
                        setAnoReferencia(parseInt(e.target.value));
                        loadConsultasViabilidade(usuarioSelecionadoParaConsultas.id, mesReferencia, parseInt(e.target.value));
                      }}
                    >
                      {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(ano => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Resumo */}
                {resumoConsultas && (
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1.5rem' 
                  }}>
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>📊 Resumo do Mês</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                      <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>{resumoConsultas.resumo?.total || 0}</div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>Total de Consultas</div>
                      </div>
                      <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>{resumoConsultas.resumo?.sucesso || 0}</div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>Sucesso</div>
                      </div>
                      <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>{resumoConsultas.resumo?.falha || 0}</div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>Falha</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Últimas Consultas */}
                <div>
                  <h3 style={{ marginBottom: '1rem' }}>📝 Últimas 100 Consultas</h3>
                  
                  {carregandoConsultas ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando...</div>
                  ) : consultasViabilidade.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      Nenhuma consulta de viabilidade encontrada
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="usuarios-table" style={{ fontSize: '0.875rem' }}>
                        <thead>
                          <tr>
                            <th>Data/Hora</th>
                            <th>CEP</th>
                            <th>Número</th>
                            <th>Status</th>
                            <th>Operadoras</th>
                          </tr>
                        </thead>
                        <tbody>
                          {consultasViabilidade.map(consulta => {
                            const operadoras = consulta.operadoras_retornadas 
                              ? Object.keys(JSON.parse(consulta.operadoras_retornadas)).join(', ')
                              : 'N/A';
                            
                            return (
                              <tr key={consulta.id}>
                                <td>
                                  {consulta.data_hora 
                                    ? new Date(consulta.data_hora).toLocaleString('pt-BR')
                                    : 'N/A'}
                                </td>
                                <td>{consulta.cep}</td>
                                <td>{consulta.numero}</td>
                                <td>
                                  <span style={{ 
                                    background: consulta.sucesso ? '#d4edda' : '#f8d7da',
                                    color: consulta.sucesso ? '#155724' : '#721c24',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem'
                                  }}>
                                    {consulta.sucesso ? '✅ Sucesso' : '❌ Falha'}
                                  </span>
                                </td>
                                <td>{operadoras || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ padding: '1rem', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeConsultasModal} className="btn-secondary">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Novo Usuário */}
        {showNovoUsuario && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-usuario">
                <h2>➕ Novo Usuário</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <form onSubmit={handleCreateSubmit} className="modal-body-usuario">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Usuário *</label>
                    <input
                      type="text"
                      name="username"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Hierarquia *</label>
                    <select 
                      name="role" 
                      required
                      value={novoUsuarioRole}
                      onChange={(e) => setNovoUsuarioRole(e.target.value)}
                    >
                      <option value="">Selecione a hierarquia</option>
                      <optgroup label="Administração">
                        {user?.role === 'admin' && <option value="admin">Administrador</option>}
                        <option value="diretor">Diretor</option>
                      </optgroup>
                      <optgroup label="Vendas">
                        <option value="coordenador">Coordenador</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="vendedor">Vendedor</option>
                      </optgroup>
                      <optgroup label="BackOffice">
                        <option value="backoffice">BackOffice</option>
                        <option value="supervisor_bko">Supervisor BKO</option>
                      </optgroup>
                    </select>
                  </div>

                  {novoUsuarioRole !== 'coordenador' && (
                    <div className="form-group">
                      <label>Supervisor</label>
                      <select name="supervisor_id">
                        <option value="">Sem supervisor</option>
                        {usuarios
                          .filter(u => {
                            if (novoUsuarioRole === 'backoffice') return u.role === 'supervisor_bko';
                            if (novoUsuarioRole === 'vendedor') return u.role === 'supervisor';
                            if (novoUsuarioRole === 'supervisor') return u.role === 'coordenador' || u.role === 'admin' || u.role === 'diretor';
                            return u.role === 'supervisor' || u.role === 'supervisor_bko' || u.role === 'admin' || u.role === 'diretor' || u.role === 'coordenador';
                          })
                          .map(sup => (
                            <option key={sup.id} value={sup.id}>{sup.nome} ({getRoleText(sup.role)})</option>
                          ))}
                      </select>
                    </div>
                  )}
                  {novoUsuarioRole === 'coordenador' && (
                    <div className="form-group full-width">
                      <label>Supervisores subordinados</label>
                      <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem' }}>
                        {usuarios.filter(u => u.role === 'supervisor').map(sup => (
                          <label key={sup.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={supervisoresSubordinados.includes(sup.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSupervisoresSubordinados(prev => [...prev, sup.id]);
                                } else {
                                  setSupervisoresSubordinados(prev => prev.filter(id => id !== sup.id));
                                }
                              }}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {sup.nome}
                          </label>
                        ))}
                        {usuarios.filter(u => u.role === 'supervisor').length === 0 && (
                          <span style={{ color: '#666' }}>Nenhum supervisor cadastrado</span>
                        )}
                      </div>
                      <small>Selecione os supervisores que reportam a este coordenador</small>
                    </div>
                  )}

                  {novoUsuarioRole === 'vendedor' && (
                    <div className="form-group">
                      <label>Chave PIX</label>
                      <input
                        type="text"
                        name="pix"
                        value={novoUsuarioPix}
                        onChange={(e) => setNovoUsuarioPix(e.target.value)}
                        placeholder="CPF, e-mail ou chave aleatória"
                      />
                      <small>Chave PIX do vendedor para pagamentos (CPF, e-mail ou chave aleatória)</small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Senha *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      minLength="6"
                    />
                    <small>Mínimo 6 caracteres</small>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Criar Usuário
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Gestão de Inatividade - Apenas Admin/Diretor */}
        {showInatividadeModal && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={closeInatividadeModal}>
            <div className="modal-content-usuario" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
              <div className="modal-header-usuario">
                <h2>Gestão de Inatividade</h2>
                <span className="close-modal" onClick={closeInatividadeModal}>×</span>
              </div>
              
              <div className="modal-body-usuario">
                {carregandoInatividade ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>Carregando...</div>
                ) : (
                  <>
                    {/* Estatísticas */}
                    {inatividadeStatus && (
                      <div style={{ 
                        background: '#fff3cd', 
                        padding: '1rem', 
                        borderRadius: '8px', 
                        marginBottom: '1.5rem' 
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>Estatísticas de Inatividade</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                          <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>
                              {inatividadeStatus.estatisticas?.vendedoresInativos || 0}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>Vendedores Inativos</div>
                          </div>
                          <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>
                              {inatividadeStatus.estatisticas?.supervisoresResponsaveis || 0}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>Supervisores Responsáveis</div>
                          </div>
                          <div style={{ background: '#fff', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                              {inatividadeStatus.estatisticas?.totalVendedoresAtivos || 0}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>Vendedores Ativos</div>
                          </div>
                        </div>
                        <p style={{ margin: '1rem 0 0 0', fontSize: '0.875rem', color: '#856404' }}>
                          Data de referência: {inatividadeStatus.dataReferencia ? new Date(inatividadeStatus.dataReferencia).toLocaleDateString('pt-BR') : 'N/A'}
                        </p>
                      </div>
                    )}

                    {/* Vendedores Inativos */}
                    {inatividadeStatus?.vendedoresInativos && inatividadeStatus.vendedoresInativos.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '2px solid #dc3545', paddingBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0 }}>Vendedores Inativos (sem venda há 15+ dias)</h3>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                            <input
                              type="checkbox"
                              checked={usuariosSelecionadosInatividade.length === inatividadeStatus.vendedoresInativos.length && inatividadeStatus.vendedoresInativos.length > 0}
                              onChange={toggleTodosInatividade}
                              style={{ marginRight: '0.5rem' }}
                            />
                            Selecionar Todos
                          </label>
                        </div>
                        <div className="table-responsive">
                          <table className="usuarios-table" style={{ fontSize: '0.875rem' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Nome</th>
                                <th>Usuário</th>
                                <th>Supervisor</th>
                                <th>Última Venda</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inatividadeStatus.vendedoresInativos.map(vendedor => (
                                <tr key={vendedor.id} style={{ background: usuariosSelecionadosInatividade.includes(vendedor.id) ? '#fff3cd' : 'transparent' }}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={usuariosSelecionadosInatividade.includes(vendedor.id)}
                                      onChange={() => toggleUsuarioInatividade(vendedor.id)}
                                    />
                                  </td>
                                  <td>{vendedor.nome}</td>
                                  <td>{vendedor.username}</td>
                                  <td>{vendedor.supervisor?.nome || '-'}</td>
                                  <td>
                                    {vendedor.ultimo_dia_venda 
                                      ? new Date(vendedor.ultimo_dia_venda).toLocaleDateString('pt-BR')
                                      : 'Nunca'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {usuariosSelecionadosInatividade.length > 0 && (
                          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8d7da', borderRadius: '8px' }}>
                            <strong>{usuariosSelecionadosInatividade.length}</strong> usuario(s) selecionado(s) para inativacao
                          </div>
                        )}
                      </div>
                    )}

                    {/* Supervisores Responsáveis */}
                    {inatividadeStatus?.supervisoresResponsaveis && inatividadeStatus.supervisoresResponsaveis.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid #ffc107', paddingBottom: '0.5rem' }}>
                          Supervisores com Subordinados Inativos
                        </h3>
                        <div className="table-responsive">
                          <table className="usuarios-table" style={{ fontSize: '0.875rem' }}>
                            <thead>
                              <tr>
                                <th>Nome</th>
                                <th>Usuário</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inatividadeStatus.supervisoresResponsaveis.map(supervisor => (
                                <tr key={supervisor.id}>
                                  <td>{supervisor.nome}</td>
                                  <td>{supervisor.username}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '1rem' }}>
                          Supervisores podem ser inativados manualmente através da edição do usuário
                        </p>
                      </div>
                    )}

                    {(!inatividadeStatus?.vendedoresInativos || inatividadeStatus.vendedoresInativos.length === 0) && (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#28a745' }}>
                        Nenhum usuário inativo encontrado no momento
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div style={{ padding: '1rem', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <button type="button" onClick={closeInatividadeModal} className="btn-secondary">
                  Fechar
                </button>
                {inatividadeStatus?.vendedoresInativos && inatividadeStatus.vendedoresInativos.length > 0 && (
                  <button 
                    type="button" 
                    onClick={inativarSelecionados} 
                    className="btn btn-danger"
                    disabled={usuariosSelecionadosInatividade.length === 0 || inativando}
                    style={{ 
                      background: usuariosSelecionadosInatividade.length === 0 ? '#ccc' : '#dc3545',
                      cursor: usuariosSelecionadosInatividade.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {inativando ? 'Inativando...' : `Inativar Selecionados (${usuariosSelecionadosInatividade.length})`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Usuarios;
